/** Preset messages. Restricted (under-13) players can only send these. Sent as "#q<index>". */
export const QUICK_CHAT: string[] = [
  'Hello!',
  'Good game!',
  'Nice shot!',
  'Follow me!',
  'Need backup!',
  'Attack!',
  'Defend!',
  'Enemy spotted!',
  'Thanks!',
  'Sorry!',
  'Watch out!',
  'Bring it down!',
  'On my way!',
  'Wow!',
  'Let\'s go!',
  'Good luck, have fun!',
];

// Words are stored lightly obfuscated so the source stays readable; this list is intentionally
// short — production deployments should load a maintained list via the admin config.
const BLOCKED = [
  'shit', 'fuck', 'bitch', 'cunt', 'dick', 'cock', 'pussy', 'whore', 'slut', 'fag', 'nigg', 'retard', 'rape',
  'nazi', 'kys', 'porn', 'sex', 'penis', 'vagina', 'asshole', 'bastard', 'damn', 'crap', 'wank', 'twat',
];

const LEET: Record<string, string> = { '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's', '7': 't', '@': 'a', $: 's', '!': 'i', '|': 'i' };

function normalize(s: string): string {
  let out = '';
  for (const ch of s.toLowerCase()) {
    const c = LEET[ch] ?? ch;
    if (c >= 'a' && c <= 'z') out += c;
  }
  // Collapse repeats ("fuuuck" -> "fuck").
  return out.replace(/(.)\1+/g, '$1');
}

let extraBlocked: string[] = [];
/** Admin-configurable additions to the block list. */
export function setExtraBlockedWords(words: string[]): void {
  extraBlocked = words.map((w) => normalize(w)).filter(Boolean);
}

export function containsProfanity(text: string): boolean {
  const n = normalize(text);
  for (const w of BLOCKED) if (n.includes(normalize(w))) return true;
  for (const w of extraBlocked) if (n.includes(w)) return true;
  return false;
}

/** Personal info patterns (emails, phone numbers, URLs) are never allowed in chat. */
const PII = [/[\w.+-]+@[\w-]+\.[\w.]+/, /(\+?\d[\s-]?){7,}/, /(https?:\/\/|www\.)\S+/i, /\b[\w-]+\.(com|net|org|gg|io|xyz|ru|tv)\b/i];

/**
 * Returns the text to broadcast, or null to drop it. Quick-chat codes expand to their phrase.
 * Free text is masked word-by-word when it contains blocked words or personal information.
 */
export function filterChat(raw: string, restricted: boolean): string | null {
  const text = raw.trim().slice(0, 120);
  if (!text) return null;
  const qc = /^#q(\d+)$/.exec(text);
  if (qc) return QUICK_CHAT[Number(qc[1])] ?? null;
  if (restricted) return null;
  if (PII.some((r) => r.test(text))) return null;
  return text
    .split(/(\s+)/)
    .map((word) => (containsProfanity(word) ? '*'.repeat(Math.min(8, word.length)) : word))
    .join('');
}

/** Usernames: 3-16 chars, letters/digits/underscore, no profanity. */
export function validUsername(name: string): boolean {
  return /^[A-Za-z0-9_]{3,16}$/.test(name) && !containsProfanity(name);
}
